import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { RadioButton } from 'primereact/radiobutton';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import { BORDER_CITIES_BY_STATE, BORDER_STATES } from '../../constants/constants';
import './WaitTimeOverridesManager.css';

const DURATION_PRESETS = [
  { label: '15 min', value: 15 },
  { label: '30 min', value: 30 },
  { label: '45 min', value: 45 },
  { label: '1 hora', value: 60 },
  { label: '2 horas', value: 120 },
  { label: '4 horas', value: 240 },
  { label: '6 horas', value: 360 },
  { label: '8 horas', value: 480 },
  { label: '12 horas', value: 720 },
  { label: '24 horas', value: 1440 },
];

const EXTEND_PRESETS = [
  { label: '+15 min', value: 15 },
  { label: '+30 min', value: 30 },
  { label: '+1 h', value: 60 },
  { label: '+2 h', value: 120 },
  { label: '+4 h', value: 240 },
];

const STATUS_OPTIONS = [
  { label: 'Dejar status de CBP', value: '' },
  { label: 'Forzar Open', value: 'Open' },
  { label: 'Forzar Closed', value: 'Closed' },
];

const PRIORITY_OPTIONS = [
  { value: 'auto', label: 'Auto' },
  { value: 'cbp', label: 'CBP' },
  { value: 'community', label: 'Comunidad' },
  { value: 'manual', label: 'Manual' },
];

function cityName(state, city) {
  const catalog = BORDER_CITIES_BY_STATE[state] || [];
  return catalog.find((item) => item.code === city)?.name || city || '—';
}

function stateName(state) {
  return BORDER_STATES.find((item) => item.code === state)?.name || state || '—';
}

function formatMins(mins) {
  if (mins == null) return '—';
  const n = Number(mins);
  if (!Number.isFinite(n)) return '—';
  if (n <= 0) return '0 min';
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

function hoursPreview(mins) {
  if (mins == null || mins === '') return 'Escribe los minutos para ver las horas';
  const n = Number(mins);
  if (!Number.isFinite(n) || n < 0) return 'Escribe los minutos para ver las horas';
  if (n === 0) return 'Se publica como 0 min (sin espera)';
  const h = Math.floor(n / 60);
  const m = n % 60;
  if (h === 0) return `Se publica como ${n} min`;
  const hoursText = h === 1 ? '1 hora' : `${h} horas`;
  if (m === 0) return `Se publica como ${hoursText} (${n} min)`;
  return `Se publica como ${hoursText} ${m} min (${n} min)`;
}

function remainingLabel(row) {
  if (!row?.live && !row?.manual_live) return 'Expirado';
  return formatMins(row.remaining_minutes ?? row.manual_remaining_minutes);
}

function sourceSeverity(source) {
  if (source === 'manual') return 'warning';
  if (source === 'community') return 'success';
  if (source === 'cbp') return 'info';
  return 'secondary';
}

function sourceLabel(source) {
  if (source === 'manual') return 'Manual';
  if (source === 'community') return 'Comunidad';
  if (source === 'cbp') return 'CBP';
  return source || '—';
}

function formatTimer(totalSeconds) {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (d > 0 || h > 0) parts.push(`${h}h`);
  if (d > 0 || h > 0 || m > 0) parts.push(`${m}min`);
  parts.push(`${s}s`);
  return parts.join(' ');
}

function trackTimer(track, nowMs) {
  const start = track?.started_at ? new Date(track.started_at).getTime() : NaN;
  if (!Number.isFinite(start)) {
    return formatTimer((Number(track?.elapsed_minutes) || 0) * 60);
  }
  const end = track.active
    ? nowMs
    : track.ended_at
      ? new Date(track.ended_at).getTime()
      : nowMs;
  return formatTimer((end - start) / 1000);
}

function confidenceLabel(value) {
  if (value === 'solid') return 'sólida';
  if (value === 'medium') return 'media';
  if (value === 'thin') return 'floja';
  if (value === 'floor') return 'piso';
  return 'ninguna';
}

function emptyForm() {
  return {
    state: null,
    city: null,
    crossing_name: null,
    transport_mode: null,
    lane_type: null,
    delay_minutes: null,
    duration_minutes: 60,
    operational_status: '',
    note: '',
  };
}

function rowKey(row) {
  return [
    row.state,
    row.city,
    row.crossing_name,
    row.transport_mode,
    row.lane_type,
  ].join('|');
}

export default function WaitTimeOverridesManager() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [boardRows, setBoardRows] = useState([]);
  const [boardCity, setBoardCity] = useState(null);
  const [openCrossings, setOpenCrossings] = useState([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const boardRequest = useRef(0);
  const [prioritySaving, setPrioritySaving] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editTarget, setEditTarget] = useState(null);
  const [editMinutes, setEditMinutes] = useState(null);
  const [editDuration, setEditDuration] = useState(60);
  const [editSaving, setEditSaving] = useState(false);
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendAdd, setExtendAdd] = useState(60);
  const [extendSaving, setExtendSaving] = useState(false);
  const [communityRow, setCommunityRow] = useState(null);
  const [communityDetail, setCommunityDetail] = useState(null);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [communityBusyId, setCommunityBusyId] = useState('');
  const [communityNow, setCommunityNow] = useState(() => Date.now());

  const loadBoard = useCallback(async (cityKey) => {
    const requestId = boardRequest.current + 1;
    boardRequest.current = requestId;
    if (!cityKey) {
      setBoardRows([]);
      setBoardLoading(false);
      return;
    }
    setBoardLoading(true);
    try {
      const [state, city] = String(cityKey).split('|');
      const { data } = await api.get('/wait_time_overrides/board', {
        params: { state, city },
      });
      if (boardRequest.current !== requestId) return;
      setBoardRows(Array.isArray(data?.rows) ? data.rows : []);
    } finally {
      if (boardRequest.current === requestId) setBoardLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    const [{ data: catalog }, { data: list }] = await Promise.all([
      api.get('/wait_time_overrides/meta'),
      api.get('/wait_time_overrides'),
    ]);
    setMeta(catalog);
    setRows(Array.isArray(list) ? list : []);
    const cities = catalog?.cities || [];
    setBoardCity((prev) => {
      if (prev) return prev;
      const tj = cities.find(
        (item) => item.state === 'BC' && (item.city === 'BC001' || /tijuana/i.test(item.title || ''))
      );
      const first = tj || cities[0];
      return first ? `${first.state}|${first.city}` : null;
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await load();
      } catch {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar el módulo de tiempos',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [load]);

  useEffect(() => {
    if (!boardCity) return;
    loadBoard(boardCity).catch(() => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar el tablero de ETA',
      });
    });
  }, [boardCity, loadBoard]);

  const cities = meta?.cities || [];
  const durationPresets = meta?.durationPresets?.length
    ? meta.durationPresets
    : DURATION_PRESETS;

  const stateOptions = useMemo(() => {
    const present = new Set(cities.map((item) => item.state).filter(Boolean));
    return BORDER_STATES.filter((item) => present.has(item.code)).map((item) => ({
      label: item.name,
      value: item.code,
    }));
  }, [cities]);

  const cityOptions = useMemo(() => {
    return cities
      .filter((item) => !form.state || item.state === form.state)
      .map((item) => ({
        label: `${cityName(item.state, item.city)} (${item.state})`,
        value: `${item.state}|${item.city}`,
        state: item.state,
        city: item.city,
      }));
  }, [cities, form.state]);

  const boardCityOptions = useMemo(
    () =>
      cities.map((item) => ({
        label: `${cityName(item.state, item.city) || item.title || item.city} (${item.state})`,
        value: `${item.state}|${item.city}`,
      })),
    [cities]
  );

  const selectedCity = useMemo(
    () => cities.find((item) => item.state === form.state && item.city === form.city) || null,
    [cities, form.state, form.city]
  );

  const crossingOptions = useMemo(
    () =>
      (selectedCity?.crossings || []).map((crossing) => ({
        label: crossing.name,
        value: crossing.name,
      })),
    [selectedCity]
  );

  const selectedCrossing = useMemo(
    () => (selectedCity?.crossings || []).find((item) => item.name === form.crossing_name) || null,
    [selectedCity, form.crossing_name]
  );

  const transportOptions = useMemo(() => {
    const seen = new Map();
    for (const lane of selectedCrossing?.lanes || []) {
      if (seen.has(lane.transport_mode)) continue;
      seen.set(lane.transport_mode, {
        label: lane.transport_label || lane.transport_mode,
        value: lane.transport_mode,
      });
    }
    return [...seen.values()];
  }, [selectedCrossing]);

  const laneOptions = useMemo(
    () =>
      (selectedCrossing?.lanes || [])
        .filter((lane) => !form.transport_mode || lane.transport_mode === form.transport_mode)
        .map((lane) => ({
          label: lane.lane_label || lane.lane_type,
          value: lane.lane_type,
          transport_mode: lane.transport_mode,
        })),
    [selectedCrossing, form.transport_mode]
  );

  const setField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'state') {
        next.city = null;
        next.crossing_name = null;
        next.transport_mode = null;
        next.lane_type = null;
      }
      if (key === 'city') {
        const [state, city] = String(value || '').split('|');
        next.state = state || prev.state;
        next.city = city || null;
        next.crossing_name = null;
        next.transport_mode = null;
        next.lane_type = null;
      }
      if (key === 'crossing_name') {
        next.transport_mode = null;
        next.lane_type = null;
      }
      if (key === 'transport_mode') {
        next.lane_type = null;
      }
      return next;
    });
  };

  const canSave =
    Boolean(form.city && form.crossing_name && form.transport_mode && form.lane_type) &&
    form.delay_minutes != null &&
    form.duration_minutes > 0 &&
    !saving;

  const refreshAll = async () => {
    await load();
    if (boardCity) await loadBoard(boardCity);
  };

  const save = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      await api.post('/wait_time_overrides', {
        state: form.state,
        city: form.city,
        crossing_name: form.crossing_name,
        transport_mode: form.transport_mode,
        lane_type: form.lane_type,
        delay_minutes: form.delay_minutes,
        duration_minutes: form.duration_minutes,
        operational_status: form.operational_status,
        note: form.note,
      });
      toast.current?.show({
        severity: 'success',
        summary: 'Activo',
        detail: `Manual fijado ${formatMins(form.duration_minutes)} en ${form.crossing_name}`,
      });
      setForm((prev) => ({ ...prev, note: '' }));
      await refreshAll();
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.error || 'No se pudo guardar el override',
      });
    } finally {
      setSaving(false);
    }
  };

  const setPriority = async (row, priority) => {
    const key = rowKey(row);
    if (prioritySaving === key || row.priority === priority) return;
    const previous = row.priority;
    setBoardRows((prev) =>
      prev.map((item) => (rowKey(item) === key ? { ...item, priority } : item))
    );
    try {
      setPrioritySaving(key);
      await api.put('/wait_time_overrides/priority', {
        state: row.state,
        city: row.city,
        crossing_name: row.crossing_name,
        transport_mode: row.transport_mode,
        lane_type: row.lane_type,
        priority,
      });
      await loadBoard(boardCity);
    } catch (err) {
      setBoardRows((prev) =>
        prev.map((item) => (rowKey(item) === key ? { ...item, priority: previous } : item))
      );
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.error || 'No se pudo cambiar la prioridad',
      });
    } finally {
      setPrioritySaving('');
    }
  };

  const endOverride = async (row) => {
    const id = row.id || row.manual_id;
    if (!id) return;
    try {
      await api.post(`/wait_time_overrides/${id}/end`);
      toast.current?.show({
        severity: 'success',
        summary: 'Terminado',
        detail: 'Manual apagado; vuelve la jerarquía Auto/CBP/Comunidad',
      });
      await refreshAll();
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo terminar el override',
      });
    }
  };

  const openEdit = (row) => {
    const target = row.override || row;
    setEditTarget({
      id: target.id || row.manual_id || null,
      state: row.state,
      city: row.city,
      crossing_name: row.crossing_name,
      transport_mode: row.transport_mode,
      lane_type: row.lane_type,
      transport_label: row.transport_label,
      lane_label: row.lane_label,
    });
    setEditMinutes(row.manual_minutes ?? target.delay_minutes ?? null);
    setEditDuration(60);
  };

  const closeEdit = () => {
    if (editSaving) return;
    setEditTarget(null);
    setEditMinutes(null);
    setEditDuration(60);
  };

  const saveEdit = async () => {
    if (!editTarget || editMinutes == null || editMinutes < 0 || editSaving) return;
    if (!editTarget.id && !(editDuration > 0)) return;
    try {
      setEditSaving(true);
      let data;
      if (editTarget.id) {
        ({ data } = await api.put(`/wait_time_overrides/${editTarget.id}`, {
          delay_minutes: editMinutes,
        }));
      } else {
        ({ data } = await api.post('/wait_time_overrides', {
          state: editTarget.state,
          city: editTarget.city,
          crossing_name: editTarget.crossing_name,
          transport_mode: editTarget.transport_mode,
          lane_type: editTarget.lane_type,
          delay_minutes: editMinutes,
          duration_minutes: editDuration,
          operational_status: '',
          note: '',
        }));
      }
      toast.current?.show({
        severity: 'success',
        summary: editTarget.id ? 'Actualizado' : 'Activo',
        detail: `${editTarget.crossing_name} ahora publica ${formatMins(editMinutes)}`,
      });
      const saved = data && typeof data === 'object' ? data : {};
      const minutes = Number(saved.delay_minutes ?? editMinutes);
      const key = rowKey(editTarget);
      const live = saved.live !== false;
      setBoardRows((prev) =>
        prev.map((row) => {
          if (rowKey(row) !== key) return row;
          const next = {
            ...row,
            manual_minutes: live ? minutes : null,
            manual_id: saved.id || row.manual_id,
            manual_live: live,
            manual_remaining_minutes:
              saved.remaining_minutes ?? row.manual_remaining_minutes,
            override: { ...(row.override || {}), ...saved, delay_minutes: minutes },
          };
          if (live && (row.priority === 'manual' || row.priority === 'auto' || !row.priority)) {
            next.public_minutes = minutes;
            next.public_source = 'manual';
            next.public_reason =
              row.priority === 'manual' ? 'prioridad manual' : 'auto: manual vigente';
          }
          return next;
        })
      );
      setRows((prev) => {
        const id = saved.id || editTarget.id;
        if (!id) return prev;
        const existing = prev.some((row) => row.id === id);
        const patched = {
          ...(prev.find((row) => row.id === id) || {}),
          ...saved,
          id,
          delay_minutes: minutes,
          live,
          remaining_minutes: saved.remaining_minutes,
        };
        if (existing) {
          return prev.map((row) => (row.id === id ? { ...row, ...patched } : row));
        }
        return live ? [patched, ...prev] : prev;
      });
      setEditTarget(null);
      setEditMinutes(null);
      setEditDuration(60);
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.error || 'No se pudo actualizar el tiempo manual',
      });
    } finally {
      setEditSaving(false);
    }
  };

  const extendOverride = async (row, minutes) => {
    const id = row.id || row.manual_id;
    if (!id) return false;
    try {
      await api.put(`/wait_time_overrides/${id}`, { duration_minutes: minutes });
      toast.current?.show({
        severity: 'success',
        summary: 'Extendido',
        detail: `Manual sigue activo ${formatMins(minutes)}`,
      });
      await refreshAll();
      return true;
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo extender el override',
      });
      return false;
    }
  };

  const openExtend = (row) => {
    setExtendTarget(row);
    setExtendAdd(60);
  };

  const closeExtend = () => {
    if (extendSaving) return;
    setExtendTarget(null);
  };

  const saveExtend = async () => {
    if (!extendTarget || extendAdd == null || extendAdd < 1 || extendSaving) return;
    const remaining = Number(extendTarget.remaining_minutes) || 0;
    const next = remaining + Number(extendAdd);
    setExtendSaving(true);
    try {
      const ok = await extendOverride(extendTarget, next);
      if (ok) setExtendTarget(null);
    } finally {
      setExtendSaving(false);
    }
  };

  const confirmEndOverride = (row) => {
    const place = row.crossing_name || 'esta garita';
    const lane = [row.transport_label, row.lane_label].filter(Boolean).join(' · ');
    confirmDialog({
      header: 'Apagar tiempo manual',
      message: lane
        ? `¿Apagar el tiempo manual de ${place} (${lane})? El cruce vuelve a Auto, CBP o comunidad.`
        : `¿Apagar el tiempo manual de ${place}? El cruce vuelve a Auto, CBP o comunidad.`,
      icon: 'pi pi-power-off',
      acceptLabel: 'Apagar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: () => endOverride(row),
    });
  };

  const removeOverride = async (row) => {
    const id = row.id || row.manual_id;
    if (!id) return;
    try {
      await api.delete(`/wait_time_overrides/${id}`);
      toast.current?.show({ severity: 'warn', summary: 'Eliminado' });
      await refreshAll();
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo eliminar',
      });
    }
  };

  const communityQuery = (row) => ({
    ports: (row?.port_numbers || []).join(','),
    transport_mode: row?.transport_mode,
    lane_type: row?.lane_type,
  });

  const openCommunity = async (row) => {
    setCommunityRow(row);
    setCommunityDetail(null);
    setCommunityLoading(true);
    try {
      const { data } = await api.get('/wait_time_overrides/community-samples', {
        params: communityQuery(row),
      });
      setCommunityDetail(data);
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los live tracks',
      });
      setCommunityRow(null);
    } finally {
      setCommunityLoading(false);
    }
  };

  const patchCommunityTrack = (id, patch) => {
    setCommunityDetail((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        tracks: (prev.tracks || []).map((track) =>
          track.id === id ? { ...track, ...patch, dirty: true } : track
        ),
      };
    });
  };

  const refreshCommunity = useCallback(async (row) => {
    if (!row) return;
    const { data } = await api.get('/wait_time_overrides/community-samples', {
      params: communityQuery(row),
    });
    setCommunityDetail((prev) => {
      if (!prev) return data;
      const dirty = new Map(
        (prev.tracks || []).filter((track) => track.dirty).map((track) => [track.id, track])
      );
      return {
        ...data,
        tracks: (data.tracks || []).map((track) => {
          const edited = dirty.get(track.id);
          if (!edited) return track;
          return {
            ...track,
            elapsed_minutes: edited.elapsed_minutes,
            path_progress: edited.path_progress,
            synthetic: edited.synthetic,
            dirty: true,
          };
        }),
      };
    });
  }, []);

  useEffect(() => {
    if (!communityRow) return undefined;
    setCommunityNow(Date.now());
    const timer = setInterval(() => setCommunityNow(Date.now()), 1000);
    const refresh = setInterval(() => {
      if (communityBusyId) return;
      refreshCommunity(communityRow).catch(() => {});
    }, 4000);
    return () => {
      clearInterval(timer);
      clearInterval(refresh);
    };
  }, [communityRow, communityBusyId, refreshCommunity]);

  const saveCommunityTrack = async (track) => {
    if (!communityRow) return;
    setCommunityBusyId(track.id);
    try {
      const { data } = await api.patch(
        `/wait_time_overrides/community-samples/${track.id}`,
        {
          ...communityQuery(communityRow),
          kind: track.kind,
          elapsed_minutes: track.elapsed_minutes,
          path_progress: track.path_progress,
          synthetic: track.synthetic,
        }
      );
      setCommunityDetail(data);
      if (boardCity) await loadBoard(boardCity);
      toast.current?.show({
        severity: 'success',
        summary: 'Comunidad recalculada',
        detail: data?.minutes == null ? 'Sin muestra' : `${data.minutes} min`,
      });
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo actualizar el live track',
      });
    } finally {
      setCommunityBusyId('');
    }
  };

  const removeCommunityTrack = async (track) => {
    if (!communityRow) return;
    setCommunityBusyId(track.id);
    try {
      const { data } = await api.delete(
        `/wait_time_overrides/community-samples/${track.id}`,
        { params: { ...communityQuery(communityRow), kind: track.kind } }
      );
      setCommunityDetail(data);
      if (boardCity) await loadBoard(boardCity);
      toast.current?.show({
        severity: 'success',
        summary: 'Comunidad recalculada',
        detail: data?.minutes == null ? 'Sin muestra' : `${data.minutes} min`,
      });
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo borrar el live track',
      });
    } finally {
      setCommunityBusyId('');
    }
  };

  const confirmRemoveCommunityTrack = (track) => {
    confirmDialog({
      header: 'Sacar del cálculo',
      message: `¿Borrar el live track de ${track.nickname}? El tiempo de comunidad se vuelve a armar sin ese registro.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Borrar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: () => removeCommunityTrack(track),
    });
  };

  const confirmRemoveActive = (row) => {
    const place = row.crossing_name || 'esta garita';
    const lane = [row.transport_label, row.lane_label].filter(Boolean).join(' · ');
    confirmDialog({
      header: 'Eliminar override activo',
      message: lane
        ? `¿Eliminar el tiempo manual de ${place} (${lane})?`
        : `¿Eliminar el tiempo manual de ${place}?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptClassName: 'p-button-danger',
      accept: () => removeOverride(row),
    });
  };

  const activeRows = rows.filter((row) => row.live);
  const pastRows = rows.filter((row) => !row.live);
  const communityPublicCount = boardRows.filter((row) => row.public_source === 'community').length;
  const manualPublicCount = boardRows.filter((row) => row.public_source === 'manual').length;

  const boardByCrossing = useMemo(() => {
    const groups = new Map();
    for (const row of boardRows) {
      const name = row.crossing_name || 'Sin crossing';
      if (!groups.has(name)) groups.set(name, []);
      groups.get(name).push(row);
    }
    return [...groups.entries()].map(([name, lanes]) => ({
      name,
      lanes,
      ports: [...new Set(lanes.flatMap((lane) => lane.port_numbers || []))],
      cityLabel:
        cityName(lanes[0]?.state, lanes[0]?.city) ||
        lanes[0]?.city_name ||
        lanes[0]?.city ||
        '',
      stateLabel: stateName(lanes[0]?.state),
    }));
  }, [boardRows]);

  const renderBoardRow = (row) => {
    const key = rowKey(row);
    const busy = prioritySaving === key;
    return (
      <tr key={key} className={busy ? 'wto-row-busy' : undefined}>
        <td>
          <strong>{row.transport_label}</strong>
          <small>{row.lane_label}</small>
        </td>
        <td>
          <span className="wto-source-mins">{formatMins(row.cbp_minutes)}</span>
        </td>
        <td>
          <div className="wto-community-cell">
            <span className="wto-source-mins wto-community">
              {formatMins(row.community_minutes)}
            </span>
            <Button
              type="button"
              icon="pi pi-cog"
              className="p-button-text p-button-sm"
              tooltip="Ver y editar live tracks"
              onClick={() => openCommunity(row)}
            />
          </div>
          <small>
            {confidenceLabel(row.community_confidence)}
            {row.community_completed ? ` · ${row.community_completed} cruces` : ''}
            {row.community_synthetic ? ` (${row.community_synthetic} sint.)` : ''}
            {row.community_active ? ` · ${row.community_active} en fila` : ''}
          </small>
          {row.community_reason ? (
            <small className="wto-reason">{row.community_reason}</small>
          ) : null}
        </td>
        <td>
          <div className="wto-community-cell">
            {row.manual_live ? (
              <span className="wto-manual">{formatMins(row.manual_minutes)}</span>
            ) : (
              <span className="wto-source-mins wto-muted">—</span>
            )}
            <Button
              type="button"
              icon="pi pi-cog"
              className="p-button-text p-button-sm"
              tooltip="Modificar tiempo manual"
              onClick={() => openEdit(row)}
            />
          </div>
          {row.manual_live ? (
            <>
              <small>quedan {remainingLabel(row)}</small>
              <div className="wto-inline-actions">
                <Button
                  type="button"
                  icon="pi pi-plus"
                  className="p-button-text p-button-sm"
                  tooltip="Extender 1 hora"
                  onClick={() =>
                    extendOverride(
                      row,
                      Math.max(60, (row.manual_remaining_minutes || 0) + 60)
                    )
                  }
                />
                <Button
                  type="button"
                  icon="pi pi-power-off"
                  className="p-button-text p-button-sm p-button-warning"
                  tooltip="Apagar manual"
                  onClick={() => confirmEndOverride(row)}
                />
                <Button
                  type="button"
                  icon="pi pi-trash"
                  className="p-button-text p-button-sm p-button-danger"
                  tooltip="Eliminar"
                  onClick={() => confirmRemoveActive(row)}
                />
              </div>
            </>
          ) : null}
        </td>
        <td>
          <strong className="wto-public-mins">{formatMins(row.public_minutes)}</strong>
          <Tag
            severity={sourceSeverity(row.public_source)}
            value={sourceLabel(row.public_source)}
          />
          <small className="wto-reason">{row.public_reason}</small>
        </td>
        <td>
          <div className="wto-priority" role="radiogroup" aria-label="Prioridad">
            {PRIORITY_OPTIONS.map((option) => (
              <label key={option.value} className="wto-priority-option">
                <RadioButton
                  inputId={`${key}-${option.value}`}
                  name={`priority-${key}`}
                  value={option.value}
                  checked={row.priority === option.value}
                  onChange={() => setPriority(row, option.value)}
                  disabled={busy}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-content-center mt-5">
        <ProgressSpinner style={{ width: '40px', height: '40px' }} />
      </div>
    );
  }

  return (
    <div className="wto-page wto-page-wide">
      <Toast ref={toast} />
      <ConfirmDialog />

      <header className="wto-header">
        <div>
          <p className="wto-kicker">Community & Mobile</p>
          <h2 className="wto-title">Tiempos de espera (CBP · Comunidad · Manual)</h2>
          <p className="wto-lead">
            Compara las tres fuentes por carril, elige cuál manda con el radio, y fija un
            manual cuando quieras forzar el número. Los cronómetros sintéticos alimentan
            Comunidad.
          </p>
        </div>
        <div className="wto-header-tags">
          <Tag
            severity={manualPublicCount ? 'warning' : 'secondary'}
            value={manualPublicCount ? `${manualPublicCount} manual` : '0 manual'}
          />
          <Tag
            severity={communityPublicCount ? 'success' : 'secondary'}
            value={
              communityPublicCount
                ? `${communityPublicCount} comunidad`
                : '0 comunidad'
            }
          />
        </div>
      </header>

      <section className="wto-card">
        <div className="wto-board-toolbar">
          <h3>Tablero ETA público</h3>
          <div className="wto-board-filters">
            <Dropdown
              value={boardCity}
              options={boardCityOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => {
                setBoardCity(e.value);
                setOpenCrossings([]);
              }}
              placeholder="Ciudad"
              filter
            />
            <Button
              type="button"
              icon="pi pi-refresh"
              className="p-button-text"
              label="Actualizar"
              onClick={() => boardCity && loadBoard(boardCity)}
            />
          </div>
        </div>
        {boardLoading ? (
          <div className="wto-board-loading">
            <ProgressSpinner style={{ width: '40px', height: '40px' }} />
          </div>
        ) : !boardCity ? (
          <p className="wto-empty">Elige una ciudad para ver el tablero.</p>
        ) : boardByCrossing.length === 0 ? (
          <p className="wto-empty">No hay carriles en esta ciudad.</p>
        ) : (
          <Accordion
            multiple
            activeIndex={openCrossings}
            onTabChange={(event) => setOpenCrossings(event.index)}
            className="wto-crossing-accordion"
          >
            {boardByCrossing.map((group) => (
              <AccordionTab
                key={group.name}
                header={
                  <span className="wto-crossing-acc-header">
                    <span>
                      <span className="wto-crossing-title">{group.name}</span>
                      <span className="wto-crossing-meta">
                        {[group.cityLabel, group.stateLabel].filter(Boolean).join(' · ')}
                        {group.ports.length
                          ? ` · puertos ${group.ports.join(', ')}`
                          : ''}
                      </span>
                    </span>
                    <Tag
                      severity="secondary"
                      value={`${group.lanes.length} carril${group.lanes.length === 1 ? '' : 'es'}`}
                    />
                  </span>
                }
              >
                <div className="wto-table-wrap">
                  <table className="wto-table wto-board-table">
                    <thead>
                      <tr>
                        <th>Carril</th>
                        <th>CBP</th>
                        <th>Comunidad</th>
                        <th>Manual</th>
                        <th>Público</th>
                        <th>Prioridad</th>
                      </tr>
                    </thead>
                    <tbody>{group.lanes.map((row) => renderBoardRow(row))}</tbody>
                  </table>
                </div>
              </AccordionTab>
            ))}
          </Accordion>
        )}
      </section>

      <section className="wto-card">
        <h3>Nuevo tiempo manual</h3>
        <div className="wto-form">
          <label className="wto-field">
            <span>Estado</span>
            <Dropdown
              value={form.state}
              options={stateOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setField('state', e.value)}
              placeholder="Todos"
              showClear
            />
          </label>
          <label className="wto-field">
            <span>Ciudad</span>
            <Dropdown
              value={form.city ? `${form.state}|${form.city}` : null}
              options={cityOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setField('city', e.value)}
              placeholder="Selecciona ciudad"
              filter
            />
          </label>
          <label className="wto-field">
            <span>Crossing name</span>
            <Dropdown
              value={form.crossing_name}
              options={crossingOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setField('crossing_name', e.value)}
              placeholder="Selecciona crossing"
              disabled={!selectedCity}
              filter
            />
          </label>
          <label className="wto-field">
            <span>Transporte</span>
            <Dropdown
              value={form.transport_mode}
              options={transportOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setField('transport_mode', e.value)}
              placeholder="Vehículo / peatonal / comercial"
              disabled={!selectedCrossing}
            />
          </label>
          <label className="wto-field">
            <span>Lane type</span>
            <Dropdown
              value={form.lane_type}
              options={laneOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setField('lane_type', e.value)}
              placeholder="Standard, SENTRI, Ready…"
              disabled={!form.transport_mode}
            />
          </label>
          <label className="wto-field">
            <span>Tiempo manual</span>
            <InputNumber
              value={form.delay_minutes}
              onChange={(e) => setField('delay_minutes', e.value)}
              onValueChange={(e) => setField('delay_minutes', e.value)}
              min={0}
              max={720}
              allowEmpty
              useGrouping={false}
              placeholder="Minutos"
            />
            <small className="wto-field-hint">{hoursPreview(form.delay_minutes)}</small>
          </label>
          <label className="wto-field">
            <span>Duración</span>
            <Dropdown
              value={form.duration_minutes}
              options={durationPresets}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setField('duration_minutes', e.value)}
              placeholder="Cuánto tiempo"
            />
          </label>
          <label className="wto-field">
            <span>Status operativo</span>
            <Dropdown
              value={form.operational_status}
              options={STATUS_OPTIONS}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setField('operational_status', e.value)}
            />
          </label>
          <label className="wto-field wto-field-wide">
            <span>Nota interna</span>
            <InputTextarea
              value={form.note}
              onChange={(e) => setField('note', e.target.value)}
              rows={2}
              autoResize
              placeholder="Ej. CBP mal reportado, fila atípica, evento…"
            />
          </label>
        </div>
        <div className="wto-form-actions">
          <p className="wto-hint">
            {selectedCrossing?.port_numbers?.length
              ? `Puertos: ${selectedCrossing.port_numbers.join(', ')}`
              : 'Elige ciudad y crossing para ver los puertos.'}
            {' '}
            Con prioridad Auto, un manual vigente manda sobre CBP y comunidad.
          </p>
          <Button
            label="Set manual time"
            icon="pi pi-clock"
            loading={saving}
            disabled={!canSave}
            onClick={save}
          />
        </div>
      </section>

      {activeRows.length > 0 && (
        <section className="wto-card">
          <h3>Manuales activos ({activeRows.length})</h3>
          <div className="wto-table-wrap">
            <table className="wto-table">
              <thead>
                <tr>
                  <th>Garita</th>
                  <th>Carril</th>
                  <th>Manual</th>
                  <th>CBP</th>
                  <th>Quedan</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {activeRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.crossing_name}</strong>
                      <small>
                        {cityName(row.state, row.city) || row.city_name || row.city}
                        {row.state ? ` · ${stateName(row.state)}` : ''}
                      </small>
                    </td>
                    <td>
                      <strong>{row.transport_label}</strong>
                      <small>{row.lane_label}</small>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="wto-manual-btn"
                        onClick={() => openEdit(row)}
                        title="Editar tiempo manual"
                      >
                        <span className="wto-manual">{formatMins(row.delay_minutes)}</span>
                      </button>
                    </td>
                    <td>{formatMins(row.cbp_minutes)}</td>
                    <td>
                      <Tag severity="warning" value={remainingLabel(row)} />
                    </td>
                    <td className="wto-actions">
                      <Button
                        type="button"
                        icon="pi pi-pencil"
                        className="p-button-text p-button-sm"
                        tooltip="Editar tiempo manual"
                        onClick={() => openEdit(row)}
                      />
                      <Button
                        type="button"
                        icon="pi pi-clock"
                        className="p-button-text p-button-sm"
                        tooltip="Extender duración"
                        onClick={() => openExtend(row)}
                      />
                      <Button
                        type="button"
                        icon="pi pi-power-off"
                        className="p-button-text p-button-sm p-button-warning"
                        tooltip="Apagar manual"
                        onClick={() => confirmEndOverride(row)}
                      />
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-text p-button-sm p-button-danger"
                        tooltip="Eliminar"
                        onClick={() => confirmRemoveActive(row)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {pastRows.length > 0 && (
        <section className="wto-card">
          <h3>Recientes</h3>
          <div className="wto-table-wrap">
            <table className="wto-table wto-table-muted">
              <thead>
                <tr>
                  <th>Garita</th>
                  <th>Carril</th>
                  <th>Manual</th>
                  <th>Notas</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pastRows.slice(0, 12).map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>{row.crossing_name}</strong>
                      <small>{cityName(row.state, row.city) || row.city_name || row.city}</small>
                    </td>
                    <td>
                      {row.transport_label} · {row.lane_label}
                    </td>
                    <td>{formatMins(row.delay_minutes)}</td>
                    <td>{row.note || '—'}</td>
                    <td className="wto-actions">
                      <Button
                        type="button"
                        icon="pi pi-trash"
                        className="p-button-text p-button-sm p-button-danger"
                        onClick={() => removeOverride(row)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Dialog
        header={
          communityRow
            ? `Comunidad · ${communityRow.crossing_name || 'Carril'} · ${[
                communityRow.transport_label,
                communityRow.lane_label,
              ]
                .filter(Boolean)
                .join(' · ')}`
            : 'Comunidad'
        }
        visible={Boolean(communityRow)}
        onHide={() => {
          if (communityBusyId) return;
          setCommunityRow(null);
          setCommunityDetail(null);
        }}
        style={{ width: 'min(980px, 96vw)' }}
      >
        {communityLoading ? (
          <div className="flex justify-content-center p-4">
            <ProgressSpinner />
          </div>
        ) : (
          <>
            <pre className="wto-formula">{communityDetail?.formula || ''}</pre>
            <div className="wto-community-estimate">
              <span>Tiempo estimado de comunidad</span>
              <Tag
                severity={communityDetail?.minutes == null ? 'secondary' : 'success'}
                value={formatMins(communityDetail?.minutes)}
              />
            </div>
            <div className="wto-table-wrap">
              <table className="wto-table wto-tracks-table">
                <thead>
                  <tr>
                    <th>Quién</th>
                    <th>Minutos</th>
                    <th>Avance</th>
                    <th>Sintético</th>
                    <th>En el cálculo</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(communityDetail?.tracks || []).length === 0 ? (
                    <tr>
                      <td colSpan={6}>No hay live tracks recientes en este carril.</td>
                    </tr>
                  ) : (
                    communityDetail.tracks.map((track) => {
                      const busy = communityBusyId === track.id;
                      return (
                        <tr key={`${track.kind}-${track.id}`}>
                          <td>
                            <strong>{track.nickname}</strong>
                            <small>
                              {track.active ? 'En fila' : 'Terminado'}
                              {track.kind === 'report' ? ' · solo reporte' : ''}
                            </small>
                          </td>
                          <td>
                            <span className="wto-track-timer">
                              {trackTimer(track, communityNow)}
                            </span>
                            <InputNumber
                              value={track.elapsed_minutes}
                              onValueChange={(e) =>
                                patchCommunityTrack(track.id, {
                                  elapsed_minutes: e.value ?? 0,
                                })
                              }
                              min={0}
                              max={720}
                              suffix=" min"
                              useGrouping={false}
                              disabled={busy}
                            />
                          </td>
                          <td>
                            <div className="wto-track-progress" aria-hidden="true">
                              <span
                                style={{
                                  width: `${Math.round((track.path_progress || 0) * 100)}%`,
                                }}
                              />
                            </div>
                            <InputNumber
                              value={Math.round((track.path_progress || 0) * 100)}
                              onValueChange={(e) =>
                                patchCommunityTrack(track.id, {
                                  path_progress: Math.min(
                                    1,
                                    Math.max(0, (e.value ?? 0) / 100)
                                  ),
                                })
                              }
                              min={0}
                              max={100}
                              suffix="%"
                              useGrouping={false}
                              disabled={busy || track.kind === 'report' || !track.active}
                            />
                            <small className="wto-track-move">
                              {`inicio ${Math.round((track.start_path_progress || 0) * 100)}% · máximo ${Math.round((track.max_path_progress || 0) * 100)}%`}
                            </small>
                          </td>
                          <td>
                            <Checkbox
                              checked={Boolean(track.synthetic)}
                              onChange={(e) =>
                                patchCommunityTrack(track.id, { synthetic: Boolean(e.checked) })
                              }
                              disabled={busy}
                            />
                          </td>
                          <td>
                            <small className="wto-track-effect">{track.effect}</small>
                          </td>
                          <td className="wto-actions">
                            <Button
                              type="button"
                              icon="pi pi-check"
                              className="p-button-text p-button-sm"
                              tooltip="Guardar y recalcular"
                              loading={busy}
                              disabled={Boolean(communityBusyId)}
                              onClick={() => saveCommunityTrack(track)}
                            />
                            <Button
                              type="button"
                              icon="pi pi-trash"
                              className="p-button-text p-button-sm p-button-danger"
                              tooltip="Borrar y recalcular"
                              disabled={Boolean(communityBusyId)}
                              onClick={() => confirmRemoveCommunityTrack(track)}
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Dialog>

      <Dialog
        header={editTarget?.id ? 'Editar tiempo manual' : 'Fijar tiempo manual'}
        visible={Boolean(editTarget)}
        onHide={closeEdit}
        style={{ width: 'min(420px, 94vw)' }}
        footer={
          <div className="wto-edit-actions">
            <Button
              type="button"
              label="Cancelar"
              className="p-button-text"
              onClick={closeEdit}
              disabled={editSaving}
            />
            <Button
              type="button"
              label="Guardar"
              icon="pi pi-check"
              loading={editSaving}
              disabled={
                editMinutes == null ||
                editMinutes < 0 ||
                (!editTarget?.id && !(editDuration > 0))
              }
              onClick={saveEdit}
            />
          </div>
        }
      >
        {editTarget ? (
          <div className="wto-edit">
            <p className="wto-edit-place">
              <strong>{editTarget.crossing_name}</strong>
              <span>
                {[editTarget.transport_label, editTarget.lane_label].filter(Boolean).join(' · ')}
              </span>
            </p>
            <label className="wto-field">
              <span>Minutos</span>
              <InputNumber
                value={editMinutes}
                onChange={(e) => setEditMinutes(e.value)}
                onValueChange={(e) => setEditMinutes(e.value)}
                min={0}
                max={720}
                useGrouping={false}
              />
              <small className="wto-field-hint">{hoursPreview(editMinutes)}</small>
            </label>
            {editTarget.id ? null : (
              <label className="wto-field">
                <span>Duración</span>
                <Dropdown
                  value={editDuration}
                  options={DURATION_PRESETS}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(e) => setEditDuration(e.value)}
                  placeholder="Cuánto tiempo"
                />
              </label>
            )}
          </div>
        ) : null}
      </Dialog>

      <Dialog
        header="Extender duración"
        visible={Boolean(extendTarget)}
        onHide={closeExtend}
        style={{ width: 'min(440px, 94vw)' }}
        footer={
          <div className="wto-edit-actions">
            <Button
              type="button"
              label="Cancelar"
              className="p-button-text"
              onClick={closeExtend}
              disabled={extendSaving}
            />
            <Button
              type="button"
              label="Extender"
              icon="pi pi-clock"
              loading={extendSaving}
              disabled={extendAdd == null || extendAdd < 1}
              onClick={saveExtend}
            />
          </div>
        }
      >
        {extendTarget ? (
          <div className="wto-edit">
            <p className="wto-edit-place">
              <strong>{extendTarget.crossing_name}</strong>
              <span>
                {[extendTarget.transport_label, extendTarget.lane_label]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </p>
            <p className="wto-extend-now">
              Quedan {formatMins(extendTarget.remaining_minutes)}. El tiempo publicado no cambia.
            </p>
            <div className="wto-extend-presets">
              {EXTEND_PRESETS.map((preset) => (
                <Button
                  key={preset.value}
                  type="button"
                  label={preset.label}
                  className={extendAdd === preset.value ? '' : 'p-button-outlined'}
                  onClick={() => setExtendAdd(preset.value)}
                />
              ))}
            </div>
            <label className="wto-field">
              <span>Minutos a sumar</span>
              <InputNumber
                value={extendAdd}
                onChange={(e) => setExtendAdd(e.value)}
                onValueChange={(e) => setExtendAdd(e.value)}
                min={1}
                max={7 * 24 * 60}
                useGrouping={false}
              />
              <small className="wto-field-hint">
                {extendAdd == null || extendAdd < 1
                  ? 'Elige cuánto tiempo extra sigue activo'
                  : `Quedarán ${formatMins(
                      (Number(extendTarget.remaining_minutes) || 0) + Number(extendAdd)
                    )}`}
              </small>
            </label>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
